import React, { useCallback, useEffect, useState } from "react";
import { API } from "../api";
import { Plus, Trash2, Pencil, Upload, X, Save, Clock } from "lucide-react";

const TYPES = ["file", "image", "url", "snippet", "person", "term", "note", "binding"];
const EMPTY = {
  type: "note", canonical: "", aliases: "", how_i_refer_to_it: "", description: "", pointer: "", cluster_id: "",
};

export default function MemoryPalace({ sid, memories, reloadMemories }) {
  const [clusters, setClusters] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showImport, setShowImport] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const loadClusters = useCallback(async () => {
    try { setClusters(await API.clusters(sid)); }
    catch (_) { setError("Could not load clusters. Please retry."); }
  }, [sid]);
  useEffect(() => { loadClusters(); }, [loadClusters]);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (m) => {
    setForm({
      ...m,
      aliases: (m.aliases || []).join(", "),
      how_i_refer_to_it: (m.how_i_refer_to_it || []).join(", "),
      cluster_id: m.cluster_id || "",
    });
    setEditing(m.id);
  };
  const save = async () => {
    if (!form.canonical.trim() || saving) return;
    setSaving(true); setError("");
    const body = {
      type: form.type, canonical: form.canonical.trim(), description: form.description,
      pointer: form.pointer, cluster_id: form.cluster_id || null,
      aliases: form.aliases.split(",").map((s) => s.trim()).filter(Boolean),
      how_i_refer_to_it: form.how_i_refer_to_it.split(",").map((s) => s.trim()).filter(Boolean),
      tags: form.tags || [],
    };
    try {
      if (editing === "new") await API.createMemory(sid, body);
      else await API.updateMemory(sid, editing, body);
      await reloadMemories();
      setEditing(null);
    } catch (_) { setError("Could not save the memory. Check the fields and try again."); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this memory?")) return;
    try { setError(""); await API.deleteMemory(sid, id); await reloadMemories(); }
    catch (_) { setError("Could not delete the memory. Please retry."); }
  };

  const byCluster = (cid) => memories.filter((m) => m.cluster_id === cid);
  const orphans = memories.filter((m) => !clusters.some((c) => c.id === m.cluster_id));
  const isStale = (m) => {
    if (!m.last_used_at) return false;
    return Date.now() - new Date(m.last_used_at).getTime() > 1000 * 60 * 60 * 24 * 3;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4">
      <div>
        {error && <div data-testid="memory-error" role="alert" className="text-rose text-sm mb-3">{error}</div>}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <h2 className="font-display font-bold text-xl">Memory Palace</h2>
          <span data-testid="memory-count" className="text-xs text-muted font-mono">{memories.length} memories</span>
          <div className="ml-auto flex gap-2">
            <button data-testid="import-btn" onClick={() => setShowImport(true)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-line text-fg/80 hover:border-teal text-sm">
              <Upload size={15} /> Import
            </button>
            <button data-testid="new-memory-btn" onClick={openNew} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber text-ink font-semibold text-sm hover:brightness-110">
              <Plus size={15} /> New memory
            </button>
          </div>
        </div>

        {[...clusters.map((c) => [c.label, c.id, byCluster(c.id)]), ["Uncategorized", null, orphans]]
          .filter(([, , list]) => list.length > 0)
          .map(([label, cid, list]) => (
            <div key={cid || "orphan"} className="mb-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 rounded-full bg-teal" />
                <h3 className="font-display font-semibold text-sm text-fg">{label}</h3>
                <span className="text-xs text-muted">{list.length}</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-2.5">
                {list.map((m) => (
                  <div key={m.id} className="bg-panel border border-line rounded-lg p-3 hover:border-line/80" data-testid={`memory-card-${m.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 rounded bg-panel2 text-teal">{m.type}</span>
                          {isStale(m) && (
                            <span className="flex items-center gap-1 text-[10px] text-amber" title="Not used recently">
                              <Clock size={10} /> stale
                            </span>
                          )}
                        </div>
                        <div data-testid={`memory-name-${m.id}`} className="font-semibold text-fg mt-1 break-words">{m.canonical}</div>
                        {m.tags?.includes("learned-from-text") && <span data-testid={`memory-learned-${m.id}`} className="text-[10px] text-lime">Learned from transcript</span>}
                        <div className="text-[11px] text-muted font-mono truncate">{m.pointer}</div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button title="Edit memory" onClick={() => openEdit(m)} className="text-muted hover:text-amber" data-testid={`edit-memory-${m.id}`}><Pencil size={14} /></button>
                        <button title="Delete memory" onClick={() => del(m.id)} className="text-muted hover:text-rose" data-testid={`delete-memory-${m.id}`}><Trash2 size={14} /></button>
                      </div>
                    </div>
                    <p className="text-xs text-fg/70 mt-1.5 line-clamp-2">{m.description || <span className="text-rose/70">no description — add one for better conceptual links</span>}</p>
                    {m.how_i_refer_to_it?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {m.how_i_refer_to_it.slice(0, 3).map((h, i) => (
                          <span key={i} className="text-[10px] font-mono text-muted bg-panel2 rounded px-1.5 py-0.5">"{h}"</span>
                        ))}
                      </div>
                    )}
                    <div className="text-[10px] text-muted font-mono mt-2">used {m.use_count || 0}×</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
      </div>

      {/* editor / import side */}
      <aside>
        {editing && (
          <div className="bg-panel border border-line rounded-xl p-4 sticky top-24" data-testid="memory-editor">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-semibold">{editing === "new" ? "New memory" : "Edit memory"}</h3>
              <button data-testid="memory-editor-close" onClick={() => setEditing(null)} className="text-muted hover:text-fg"><X size={16} /></button>
            </div>
            <div className="space-y-2.5 text-sm">
              <Field label="Canonical">
                <input data-testid="mem-canonical" value={form.canonical} onChange={(e) => setForm({ ...form, canonical: e.target.value })} className={inputCls} />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Type">
                  <select data-testid="mem-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls}>
                    {TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Cluster">
                  <select data-testid="mem-cluster" value={form.cluster_id} onChange={(e) => setForm({ ...form, cluster_id: e.target.value })} className={inputCls}>
                    <option value="">none</option>
                    {clusters.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Pointer (path / url / email)">
                <input data-testid="mem-pointer" value={form.pointer} onChange={(e) => setForm({ ...form, pointer: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Description (helps conceptual links)">
                <textarea data-testid="mem-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className={inputCls} />
              </Field>
              <Field label="Aliases (comma separated)">
                <input data-testid="mem-aliases" value={form.aliases} onChange={(e) => setForm({ ...form, aliases: e.target.value })} className={inputCls} />
              </Field>
              <Field label="How I refer to it (comma separated)">
                <input data-testid="mem-refer" value={form.how_i_refer_to_it} onChange={(e) => setForm({ ...form, how_i_refer_to_it: e.target.value })} className={inputCls} />
              </Field>
              <button data-testid="save-memory-btn" onClick={save} disabled={!form.canonical.trim() || saving} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-amber text-ink font-semibold hover:brightness-110 disabled:opacity-40">
                <Save size={15} /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}
        {showImport && <ImportPanel sid={sid} clusters={clusters} onClose={() => setShowImport(false)} reload={reloadMemories} />}
        {!editing && !showImport && (
          <div className="bg-panel border border-line rounded-xl p-4 text-sm text-muted">
            Select a memory to edit, or add a new one. Rich descriptions and "how I refer to it" phrases make conceptual linking work.
          </div>
        )}
      </aside>
    </div>
  );
}

const inputCls = "w-full bg-ink border border-line rounded-lg px-2.5 py-2 text-sm outline-none focus:border-teal/60";
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] text-muted font-mono">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function ImportPanel({ sid, clusters, onClose, reload }) {
  const [format, setFormat] = useState("json");
  const [content, setContent] = useState("");
  const [cluster, setCluster] = useState("");
  const [msg, setMsg] = useState("");
  const placeholder = {
    json: '[{"canonical":"schema.sql","type":"file","description":"DB schema","aliases":["the schema"]}]',
    csv: "canonical,type,description,aliases\nschema.sql,file,DB schema,the schema|db schema",
    markdown: "- Vector DB: stores embeddings for search\n- Latency budget: p95 target per tier",
  };
  const run = async () => {
    try {
      const r = await API.importMemories(sid, { format, content, cluster_id: cluster || null });
      setMsg(`Imported ${r.count} memories`);
      await reload();
      setContent("");
    } catch (e) {
      setMsg(e.response?.data?.detail || "Import failed");
    }
  };
  return (
    <div className="bg-panel border border-line rounded-xl p-4" data-testid="import-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-semibold">Import memories</h3>
        <button data-testid="import-close" onClick={onClose} className="text-muted hover:text-fg"><X size={16} /></button>
      </div>
      <div className="flex gap-1.5 mb-2">
        {["json", "csv", "markdown"].map((f) => (
          <button key={f} data-testid={`import-format-${f}`} onClick={() => setFormat(f)}
            className={`text-xs px-2.5 py-1 rounded font-mono ${format === f ? "bg-teal text-ink" : "bg-panel2 text-muted"}`}>{f}</button>
        ))}
      </div>
      <select data-testid="import-cluster-select" value={cluster} onChange={(e) => setCluster(e.target.value)} className={inputCls + " mb-2"}>
        <option value="">assign cluster (optional)</option>
        {clusters.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
      </select>
      <textarea data-testid="import-textarea" value={content} onChange={(e) => setContent(e.target.value)} rows={7}
        placeholder={placeholder[format]} className={inputCls + " font-mono text-xs"} />
      <button data-testid="run-import-btn" onClick={run} disabled={!content.trim()} className="w-full mt-2 py-2 rounded-lg bg-amber text-ink font-semibold text-sm disabled:opacity-40">Import</button>
      {msg && <div className="text-xs text-lime mt-2" data-testid="import-msg">{msg}</div>}
    </div>
  );
}
